import type { UserRole } from "@teamz/common";
import type { UrlKeys } from "nuqs";

import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { userRoles } from "@teamz/common";
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RootField, TextField } from "@/components/ui/text-field";
import { useTRPC } from "@/lib/trpc";

import { columns } from "./users-table/columns";
import { DataTable } from "./users-table/data-table";

const filterParams = {
  role: parseAsStringLiteral(userRoles),
  search: parseAsString.withDefault(""),
  page: parseAsInteger.withDefault(1),
  pageSize: parseAsInteger.withDefault(10),
};

const filterParamsKeys: UrlKeys<typeof filterParams> = {
  role: "role",
  search: "search",
  page: "page",
  pageSize: "pageSize",
};

function downloadCSV(csvContent: string, filename: string = "export.csv"): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
}

export function AdminDetailedUserList() {
  const [{ role, search, page, pageSize }, setFilterParams] = useQueryStates(filterParams, { urlKeys: filterParamsKeys });
  const trpc = useTRPC();
  const { data, status } = useQuery(trpc.user.getDetailedUsers.queryOptions(
    { page, pageSize, role, search },
    { placeholderData: keepPreviousData },
  ));
  const { mutate, status: st } = useMutation(trpc.user.exportData.mutationOptions({
    onSuccess: async (data) => {
      downloadCSV(data.result, `users-export-${new Date().toISOString().split("T")[0]}.csv`);
    },
  }));
  const totalPages = Math.ceil((data?.total ?? 0) / Number(pageSize));

  if (status === "error") {
    return null;
  }

  const handleExport = () => {
    mutate({
      search,
      role,
    });
  };

  return (
    <div className="flex h-full flex-col gap-4 pt-4">
      <div className="flex flex-col items-center justify-between gap-2 sm:flex-row">
        <div className="flex w-full flex-col items-center gap-2 sm:flex-row">
          <RootField className="w-full sm:max-w-96">
            <TextField
              clearable
              onClear={() => {
                setFilterParams({ search: "" });
              }}
              value={search}
              className="text-neutral-400"
              placeholder="Rechercher..."
              onChange={event => setFilterParams({ search: event.target.value, page: 1 })}
            />
          </RootField>
          <Select value={role ?? ""} onValueChange={value => setFilterParams({ role: value as UserRole, page: 1 })}>
            <SelectTrigger className="w-full text-neutral-400 sm:max-w-56">
              <SelectValue placeholder="Sélectionner un rôle" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {/* @ts-expect-error type error on null value */}
                <SelectItem value={null} className="font-semibold">Voir tout</SelectItem>
                <SelectSeparator />
                <SelectItem value="player">Player</SelectItem>
                <SelectItem value="club">Club</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          onClick={handleExport}
          loading={st === "pending"}
          disabled={st === "pending" || data?.users.length === 0}
          className="w-48"
        >
          Exporter (
          {`${data ? data.total : "-"} users`}
          )
        </Button>
      </div>

      <div className="flex flex-col gap-1">
        <span className="px-2 text-xs text-neutral-500">
          Users (
          {data ? data.total : "-"}
          )
        </span>

        <DataTable
          columns={columns}
          data={data?.users || []}
          page={page}
          setPage={page => setFilterParams({ page })}
          pageSize={pageSize}
          setPageSize={pageSize => setFilterParams({ pageSize })}
          totalPages={totalPages}
        />
      </div>
    </div>
  );
}
